/**
 * Handler: variant_change event
 */

import type { WindowAnalyzify } from '../../types/window-analyzify';
import type { StorefrontProduct } from '../../types/storefront';
import { adaptProduct } from '../../adapters/product';
import { sendGA4Fallback } from '../fallbacks/ga4';
import { gtmMarketFields } from '../utils';

export function handleVariantChange(
  analyzify: WindowAnalyzify,
  product: StorefrontProduct,
  variantId: string
): void {
  try {
    const { product: adaptedProduct, variant, taxonomy, collection } = adaptProduct(product, variantId);

    // CRITICAL: Ensure getProductObj exists - CDN's gaVariantChange reads from it
    // If setPageContext wasn't called first, create the structure now
    if (!(analyzify as any).getProductObj) {
      (analyzify as any).getProductObj = {
        product: adaptedProduct,
        variant,
        taxonomy,
        collection,
      };
    } else {
      // Update existing getProductObj with new variant
      (analyzify as any).getProductObj.variant = variant;
      (analyzify as any).getProductObj.product = adaptedProduct;
    }

    // Update window.analyzify.product state (CDN scripts also read from this)
    const priceInDollars = variant.price / 100;
    if (!(analyzify as any).product) {
      (analyzify as any).product = adaptedProduct;
    }
    (analyzify as any).product.variantInput = String(variant.id);
    (analyzify as any).product.variantAvailable = variant.available;
    (analyzify as any).product.variant_title = variant.title;
    (analyzify as any).product.value = priceInDollars;
    (analyzify as any).product.revenue_value = priceInDollars;

    // GA4 - Try CDN function first, fall back to direct gtag call if it fails
    const props = analyzify.properties;
    let ga4Tracked = false;

    if (typeof analyzify.gaVariantChange === 'function') {
      try {
        // CDN reads variantData.id to match against getProductObj.product.variants
        analyzify.gaVariantChange(variant);
        ga4Tracked = true;
      } catch {
        // CDN function failed (expected in headless SPA), will fall back to direct gtag call
        // Silently catch - CDN scripts expect Liquid-specific context that doesn't exist in headless
      }
    }

    // Fall back to direct gtag call if CDN failed or doesn't exist
    // CDN uses flat fields (not items array) and event name 'variant_changed'
    if (!ga4Tracked) {
      sendGA4Fallback(analyzify, 'variant_changed', {
        variant_id: variant.id?.toString() || null,
        variant_title: variant.title || null,
        product_price: analyzify.formatPrice?.(variant.price, true) ?? variant.price / 100,
        product_sku: variant.sku || null,
        product_id: adaptedProduct.id || null,
        product_title: adaptedProduct.title || null,
        variant_availability: variant.available ?? false,
      });
    }

    // GTM - Try CDN function, fall back to dataLayer push
    let gtmTracked = false;
    if (typeof analyzify.gtmVariantChange === 'function') {
      try {
        // CDN reads variantData.id to match against getProductObj.product.variants
        analyzify.gtmVariantChange(variant);
        gtmTracked = true;
      } catch {
        // CDN function failed (expected in headless SPA), will fall back to dataLayer push
      }
    }

    // Fall back to dataLayer push if CDN failed or doesn't exist
    // CDN structure: flat fields (not ecommerce.items[])
    if (!gtmTracked && Array.isArray((window as any).dataLayer) && props?.GTM?.status) {
      (window as any).dataLayer.push({
        event: 'ee_variant_changed',
        ss_event_id: analyzify.eventId.get('variant_changed') || null,
        variant_id: variant.id?.toString() || null,
        variant_title: variant.title || null,
        product_price: analyzify.formatPrice?.(variant.price, true) ?? variant.price / 100,
        product_sku: variant.sku || null,
        product_id: adaptedProduct.id || null,
        product_title: adaptedProduct.title || null,
        cart_id: analyzify.cart_id || null,
        variant_availability: variant.available ?? false,
        ...gtmMarketFields(analyzify),
      });
    }
  } catch (error) {
    console.error('Error in trackVariantChange:', error);
  }
}
